import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ImageOff, Loader2, Sparkles, Square } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { clearRestoreCache } from "@/lib/restore";

export function useBulkJob(base, { noun, onDone }) {
  const [status, setStatus] = useState(null);
  const wasRunning = useRef(false);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data: st } = await api.get(base);
        if (!alive) return;
        setStatus(st);
        if (wasRunning.current && !st.running) {
          clearRestoreCache();
          const failed = st.skipped.filter((x) => x.reason === "error").length;
          if (st.error) toast.error(st.error);
          else toast.success(`Saved ${noun} for ${st.fixed} of ${st.total}${failed ? ` — ${failed} failed` : ""}${st.skipped.length - failed ? ` — ${st.skipped.length - failed} need a manual pick` : ""}`);
          onDone?.(st);
        }
        wasRunning.current = st.running;
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    try { const { data: st } = await api.post(base); setStatus(st); wasRunning.current = true; } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancel = () => api.post(`${base}/cancel`).catch(() => {});
  return { status, running: !!status?.running, start, cancel };
}

export const BulkButton = ({ job, label, testid }) => (job.running
  ? <button type="button" onClick={job.cancel} data-testid={`${testid}-cancel`} className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-sm"><Square size={13} />Stop</button>
  : <button type="button" onClick={job.start} data-testid={testid} className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm font-medium text-white transition-transform hover:scale-[1.03]"><Sparkles size={14} />{label}</button>);

const label = (x) => x.artist ? `${x.artist} — ${x.name}` : x.name;

/* Progress + outcome of a bulk art job. Everything found is saved automatically; skipped items are listed with the reason. */
export const BulkProgress = ({ job, noun, testid, onRetry }) => {
  const st = job.status;
  if (!st || !(st.running || st.finished_at)) return null;
  const pct = st.total ? Math.round((st.done / st.total) * 100) : 0;
  const failed = st.skipped.filter((x) => x.reason === "error");
  const unmatched = st.skipped.filter((x) => x.reason !== "error");
  const FALLBACK_LABEL = { sibling: "another cover by the same artist", artist: "the artist photo", album: "one of their album covers", generated: "a generated tile" };
  const fallbackKinds = [...new Set(st.skipped.map((x) => x.fallback).filter(Boolean))].map((k) => `${st.skipped.filter((x) => x.fallback === k).length} × ${FALLBACK_LABEL[k] || k}`);
  const reasons = [...new Set(failed.map((x) => x.error).filter(Boolean))];
  return (
    <div className="mb-8 rounded-lg border border-zinc-200 bg-white px-6 py-4" data-testid={testid}>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2">{st.running && <Loader2 size={14} className="animate-spin" />}
          {st.running ? <>Searching {st.done} / {st.total}{st.current ? <span className="text-zinc-500"> · {st.current}</span> : null}</>
            : st.error ? <><AlertTriangle size={14} className="text-amber-600" />{st.error}</>
            : <><CheckCircle2 size={14} className="text-emerald-600" />{st.cancelled ? "Stopped — " : "Done — "}saved {noun} for <strong>{st.fixed}</strong> of {st.total}</>}
        </span>
        <span className="font-mono text-xs text-zinc-500">{pct}%</span>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-zinc-100"><div className="h-full bg-zinc-900 transition-[width]" style={{ width: `${pct}%` }} /></div>
      {!st.running && (
        <div className="mt-3 space-y-2 text-xs text-zinc-500" data-testid={`${testid}-summary`}>
          {st.fixed > 0 && <p data-testid={`${testid}-saved-note`}>Saved automatically — no extra step needed. Your library view refreshes on its own.{st.local > 0 && <> <strong>{st.local}</strong> came from your own files (folder image or embedded art).</>}</p>}
          {st.fuzzy > 0 && (
            <p data-testid={`${testid}-fuzzy`}>
              <strong>{st.fuzzy} saved by close match</strong> (punctuation, edition tag or a one-letter difference): {(st.fuzzy_items || []).slice(0, 6).map((x) => `${label(x)} → ${x.picked}`).join("; ")}{(st.fuzzy_items || []).length > 6 ? ` and ${st.fuzzy_items.length - 6} more` : ""}. Wrong one? Replace it from the album or artist page.
            </p>
          )}
          {failed.length > 0 && (
            <p className="flex items-start gap-1.5 text-amber-700" data-testid={`${testid}-failed`}>
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span><strong>{failed.length} failed</strong>{reasons.length ? ` (${reasons.join("; ")})` : ""}: {failed.slice(0, 6).map(label).join(", ")}{failed.length > 6 ? ` and ${failed.length - 6} more` : ""}.{onRetry && <> <button type="button" onClick={onRetry} className="underline underline-offset-2" data-testid={`${testid}-retry`}>Run again</button></>}</span>
            </p>
          )}
          {unmatched.length > 0 && (
            <p data-testid={`${testid}-skipped`}>
              <strong>{unmatched.length} without a confident match</strong> (exact and close matches are applied automatically — pick these by hand or from the near misses): {unmatched.slice(0, 8).map(label).join(", ")}{unmatched.length > 8 ? ` and ${unmatched.length - 8} more` : ""}.
            </p>
          )}
          {st.fallback > 0 && (
            <p className="flex items-start gap-1.5 text-zinc-600" data-testid={`${testid}-fallback`}>
              <ImageOff size={12} className="mt-0.5 shrink-0" />
              <span><strong>{st.fallback} given a stand-in image</strong> so nothing shows blank{fallbackKinds.length ? ` (${fallbackKinds.join(", ")})` : ""}. Running this again swaps them for real artwork whenever one turns up, or pick one by hand any time.</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};
